import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft,
  Package,
  CreditCard,
  Calendar,
  User,
  DollarSign,
  Receipt,
  ShoppingBag,
  AlertCircle,
  Loader2,
  ShoppingBasket,
  ShoppingCart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { clientService } from '@/services/client.service';
import { VendaCliente } from '@/types/venda-cliente';

const Pedidos: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [vendas, setVendas] = useState<VendaCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    loadVendas();
  }, []);

  const loadVendas = async () => {
    const userDataString = localStorage.getItem('userlogged');
    let parsedUserData;
    if (userDataString) {
      parsedUserData = JSON.parse(userDataString);
      setUserData(parsedUserData);
    }

    if (!parsedUserData?.id) {
      toast({
        title: "Erro",
        description: "Usuário não encontrado. Faça login novamente.",
        variant: "destructive",
      });
      navigate('/authentication/side-login');
      return;
    }

    setLoading(true);
    try {
      const data = await clientService.getVendasCliente(parsedUserData.id);
      setVendas(data);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar seus pedidos.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return (value / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: { [key: string]: string } = {
      'credit_card': 'Cartão de Crédito',
      'pix': 'PIX',
      'boleto': 'Boleto',
      'debit_card': 'Cartão de Débito',
      'money': 'Dinheiro'
    };
    return methods[method] || method;
  };

  const getPaymentMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      'credit_card': 'bg-blue-100 text-blue-800',
      'pix': 'bg-green-100 text-green-800',
      'boleto': 'bg-orange-100 text-orange-800',
      'debit_card': 'bg-purple-100 text-purple-800',
      'money': 'bg-gray-100 text-gray-800'
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  const totalGasto = vendas.reduce((total, venda) => total + venda.amount, 0);
  const totalPedidos = vendas.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-12">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate('/home-cliente')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Meus Pedidos</h1>
            <p className="text-sm text-gray-500">Histórico de compras</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-6">
        {/* Cards de Resumo */}
        {/* <div className="grid grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-200 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-xs text-green-700 font-medium">Total Gasto</p>
                  <p className="text-lg font-bold text-green-900">{formatCurrency(totalGasto)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <Package className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">Total Pedidos</p>
                  <p className="text-lg font-bold text-blue-900">{totalPedidos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}

        {/* Lista de Pedidos */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Histórico de Compras
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-gray-500">Carregando pedidos...</p>
              </div>
            </div>
          ) : vendas.length > 0 ? (
            <div className="space-y-3">
              {vendas.map((venda) => (
                <Card key={venda.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Header do pedido */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-success/10 rounded-lg">
                            <Package className="w-4 h-4 text-success" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              Pedido #{venda.codepagarme}
                            </p>
                            <p className="text-xs text-gray-500">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(venda.date_created)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {formatCurrency(venda.amount)}
                          </p>
                          {venda.installments > 0 && (
                            <p className="text-xs text-gray-500">
                              {venda.installments}x
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Detalhes */}
                      <div className="space-y-2">
                        {/* Método de pagamento */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Pagamento:</span>
                          <Badge className={`text-xs ${getPaymentMethodColor(venda.payment_method)}`}>
                            {getPaymentMethodLabel(venda.payment_method)}
                          </Badge>
                        </div>

                        {/* Revendedora */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Revendedora:</span>
                          <div className="flex items-center gap-1 text-sm">
                            <User className="w-3 h-3 text-gray-400" />                          
                            <span className="text-gray-500">#{venda.revendedora.codigo}</span>
                            <span className="font-medium">{venda.revendedora.nome.split(" ")[0]}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Você ainda não fez nenhuma compra. Entre em contato com uma revendedora para fazer seu primeiro pedido!
              </AlertDescription>
            </Alert>
          )}
        </div>
        {!loading && vendas.length !== 0 && (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                    <ShoppingCart className="w-4 h-4" />
                    Como fazer uma compra
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                    <p>
                    • <strong>Entre em contato:</strong> Converse com uma revendedora autorizada, para conhecer os produtos disponíveis.
                    </p>
                    <p>
                    • <strong>Escolha e pague pelo aplicativo:</strong> após escolher os itens que deseja comprar, finalize o pagamento de forma prática e segura pelo aplicativo da revendedora informando seu CPF para que o sistema registre a compra.
                    </p>
                    <p>
                    • <strong>Ganhe diamantes:</strong> A cada compra registrada pelo seu CPF, você acumula diamantes que podem ser trocados por produtos exclusivos na Loja de Recompensas.
                    </p>
                    <hr></hr>
                    <p>
                    • <strong>Dica:</strong> Se precisar de ajuda para encontrar uma revendedora próxima a você, fale conosco pelo menu lateral na opção "Suporte".
                    </p>
                </CardContent>
            </Card>
        )}
        
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>
    </div>
  );
};

export default Pedidos;
